/**
 * "Forgot password" request screen.
 *
 * Submits an email address to /auth/forgot-password. The backend deliberately
 * returns the same response whether or not an account exists, so this page
 * shows one confirmation either way — never "no such account", which would let
 * anyone probe for registered addresses.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../services/apiService';
import cegLogo from '../assets/CEG Logo.svg';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // 429 is the rate limiter; anything else is unexpected.
      setError(
        err?.response?.status === 429
          ? 'Too many reset requests. Please wait a while and try again.'
          : 'Something went wrong. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src={cegLogo} alt="CEG" style={styles.logoImg} />
        </div>

        {sent ? (
          <>
            <h1 style={styles.title}>Check your email</h1>
            <p style={styles.subtitle}>
              If an account exists for <strong>{email.trim()}</strong>, a reset link
              is on its way. The link expires in one hour.
            </p>
            <p style={styles.hint}>
              Nothing arrived? Check your spam folder, or confirm you used the same
              address you sign in with.
            </p>
            <Link to="/login" style={styles.backBtn}>Back to sign in</Link>
          </>
        ) : (
          <>
            <h1 style={styles.title}>Reset password</h1>
            <p style={styles.subtitle}>
              Enter your email and we'll send you a link to choose a new password.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <button type="submit" style={styles.submitBtn} disabled={loading || !email.trim()}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p style={styles.applyLink}>
              <Link to="/login" style={styles.link}>Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-mint)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-6)',
  },
  card: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-modal)',
    padding: 'var(--space-10)',
    width: '100%',
    maxWidth: 400,
  },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 30, fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', marginBottom: 'var(--space-1)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.75, textAlign: 'center', marginBottom: 'var(--space-8)', lineHeight: 1.5 },
  hint: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.55, textAlign: 'center', marginBottom: 'var(--space-6)', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  input: {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--color-mint-dark)',
    background: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-forest)',
    outline: 'none',
  },
  error: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', textAlign: 'center', margin: 0 },
  submitBtn: {
    marginTop: 'var(--space-2)',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
  },
  backBtn: {
    display: 'block',
    textAlign: 'center',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    textDecoration: 'none',
  },
  applyLink: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'center', marginTop: 'var(--space-6)', opacity: 0.75 },
  link: { color: 'var(--color-forest)', fontWeight: 700 },
};
